<template>
	<div
		id="relationsView"
		:class="{ animate: animateView }"
		:style="{ 'animation-delay': animationDelay }"
		class="content-container"
	>
		<!-- One full-width panel: the overview, or a dossier -->
		<!-- Right: Dossier -->
		<section id="npc-detail" :class="{ animate: animate }" class="section-container">
			<div style="height: 52px; overflow: hidden">
				<div class="section-header clipped-medium-backward-events-logs">
					<img src="/icons/npc.svg" />
					<h1>The Milieu</h1>
				</div>
				<div class="rhombus-back">&nbsp;</div>
			</div>
			<div class="section-content-container extra-margins">
				<div ref="dossier" class="npc-dossier" v-if="selectedNpc">
					<button class="dossier-back" @click="selectedNpc = null">◀ ALL PERSONS</button>
					<div class="dossier-top">
						<div class="dossier-left">
							<div class="dossier-header">
								<h1>{{ factionOf(selectedNpc) }} // {{ selectedNpc.pronouns }}</h1>
								<h2>{{ selectedNpc.name }}</h2>
								<div class="npc-badges dossier-badges">
									<span
										v-if="selectedNpc.status !== 'alive'"
										class="status-badge"
										:class="selectedNpc.status"
										>{{ selectedNpc.status }}</span
									>
									<span v-if="isSotw(selectedNpc)" class="origin-tag" title="Shadow of the Wolf"
										>Shadow of the Wolf</span
									>
								</div>
								<span class="dossier-title">{{ selectedNpc.title }}</span>
							</div>
							<div class="relationship-log">
								<div class="relationship-log-header">
									<img src="/icons/clock.svg" class="log-icon" />
									<span>Relationship Log</span>
								</div>
								<div class="relationship-log-entries">
									<div
										v-if="selectedNpc.log && selectedNpc.log.length"
										v-for="(entry, i) in selectedNpc.log"
										:key="i"
										class="log-entry"
									>
										<span class="log-delta" :class="entry.delta > 0 ? 'log-up' : 'log-down'"
											>{{ entry.delta > 0 ? "+" : "" }}{{ entry.delta }}</span
										>
										<span class="log-text">{{ entry.event }}</span>
									</div>
									<div v-else class="log-empty">// NO ENTRIES //</div>
								</div>
							</div>
						</div>
						<div class="dossier-clock-wrapper">
							<RelationshipClock
								:value="selectedNpc.clockValue"
								:large="true"
								:mood="strippedMood"
							/>
						</div>
						<div class="dossier-portrait-wrapper">
							<img :src="portraitSrc" class="dossier-portrait" />
						</div>
					</div>

					<div class="dossier-body">
						<vue-markdown-it
							v-if="selectedNpc.notes"
							:source="selectedNpc.notes"
							class="markdown"
						/>
					</div>
				</div>

				<!-- Quick glance at everyone matching the current filters -->
				<div class="overview" v-else>
					<div class="overview-toolbar">
						<label class="search-field">
							<i class="mdi mdi-magnify search-icon" aria-hidden="true"></i>
							<input
								v-model="search"
								class="search-input"
								placeholder="Search name, title, house..."
								type="text"
							/>
						</label>
						<div class="filter-group">
							<span class="stat-label">FACTION</span>
							<select v-model="factionFilter" class="search-input filter-select">
								<option value="all">All factions</option>
								<option v-for="faction in uniqueFactions" :key="faction" :value="faction">
									{{ faction }}
								</option>
							</select>
						</div>
						<div v-if="filtersActive" class="filter-summary">
							<button class="filter-clear" @click="clearFilters">CLEAR FILTERS</button>
						</div>
					</div>
					<div ref="stats" class="overview-stats">
						<div class="overview-stat">
							<span class="stat-label">Status</span>
							<div class="overview-figures">
								<div class="overview-figure alive">
									<span class="overview-value">{{ statusCounts.alive }}</span
									><span>Alive</span>
								</div>
								<div class="overview-figure deceased">
									<span class="overview-value">{{ statusCounts.deceased }}</span
									><span>Deceased</span>
								</div>
								<div class="overview-figure unknown">
									<span class="overview-value">{{ statusCounts.unknown }}</span
									><span>Unknown</span>
								</div>
							</div>
						</div>
						<div class="overview-stat">
							<span class="stat-label">Standing</span>
							<div class="overview-figures">
								<div class="overview-figure zone-friendly">
									<span class="overview-value">{{ zoneCounts.Friendly }}</span
									><span>Friendly</span>
								</div>
								<div class="overview-figure zone-neutral">
									<span class="overview-value">{{ zoneCounts.Neutral }}</span
									><span>Neutral</span>
								</div>
								<div class="overview-figure zone-frosty">
									<span class="overview-value">{{ zoneCounts.Frosty }}</span
									><span>Frosty</span>
								</div>
							</div>
						</div>
					</div>
					<div ref="overviewGrid" class="overview-grid">
						<div
							v-for="npc in filteredNpcs"
							:key="npc.id"
							class="overview-card"
							:class="npc.status"
							@click="selectNpc(npc)"
						>
							<div class="portrait-frame">
								<img
									:src="npc.portrait || './nodata.png'"
									class="overview-portrait"
									@error="e => (e.target.src = './nodata.png')"
								/>
								<span v-if="npc.status === 'deceased'" class="portrait-stamp deceased"
									>Deceased</span
								>
								<span v-else-if="npc.status === 'unknown'" class="portrait-stamp unknown"
									>Whereabouts<br />unknown</span
								>
							</div>
							<div class="overview-info">
								<div class="overview-topline">
									<h1>{{ factionOf(npc) }}</h1>
									<span class="npc-pronouns">{{ npc.pronouns }}</span>
								</div>
								<h2>{{ npc.shortName }}</h2>
								<span class="npc-title"
									>{{ npcZone(npc.clockValue) }}, {{ npcMood(npc.clockMood) }}</span
								>
								<div class="overview-meter">
									<div
										class="overview-meter-fill"
										:class="'zone-' + npcZone(npc.clockValue).toLowerCase()"
										:style="{ width: npc.clockValue * 10 + '%' }"
									></div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="filteredNpcs.length === 0" class="npc-list-empty">
						<p>// NO PERSONS MATCH FILTER //</p>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import { VueMarkdownIt } from "@f3ve/vue-markdown-it";
import { npcs } from "@/data/npcs.js";
import { withLiveNpc } from "@/data/liveState.js";
import RelationshipClock from "@/components/RelationshipClock.vue";
import { animate, stagger } from "motion";
import { retrieveRows, revealRecord, countUp, reducedMotion } from "@/utils/retrieve";
import { onEscape } from "@/utils/escape";

const EASE_OUT = [0.22, 1, 0.36, 1];

export default {
	components: { RelationshipClock, VueMarkdownIt },
	props: {
		animate: {
			type: Boolean,
			required: true,
		},
	},
	data() {
		return {
			selectedNpc: null,
			animateView: this.animate,
			animationDelay: "1.75s",
			search: "",
			factionFilter: "all",
		};
	},
	computed: {
		// Relationship clocks linked in Foundry show its values (src/data/liveState.js)
		npcs() {
			return npcs.map(withLiveNpc);
		},
		uniqueFactions() {
			return [...new Set(this.npcs.map(this.factionOf))].sort();
		},
		filteredNpcs() {
			const query = this.search.trim().toLowerCase();
			return this.npcs
				.filter(npc => {
					if (this.factionFilter !== "all" && this.factionOf(npc) !== this.factionFilter)
						return false;
					if (query) {
						const haystack = [npc.name, npc.shortName, npc.title, npc.affiliation]
							.join(" ")
							.toLowerCase();
						if (!haystack.includes(query)) return false;
					}
					return true;
				})
				.sort((a, b) => a.shortName.localeCompare(b.shortName));
		},
		filtersActive() {
			return this.search !== "" || this.factionFilter !== "all";
		},
		statusCounts() {
			const counts = { alive: 0, deceased: 0, unknown: 0 };
			this.filteredNpcs.forEach(npc => counts[npc.status]++);
			return counts;
		},
		zoneCounts() {
			const counts = { Friendly: 0, Neutral: 0, Frosty: 0 };
			this.filteredNpcs.forEach(npc => counts[this.npcZone(npc.clockValue)]++);
			return counts;
		},
		strippedMood() {
			if (!this.selectedNpc) return "";
			return this.npcMood(this.selectedNpc.clockMood);
		},
		portraitSrc() {
			if (!this.selectedNpc || !this.selectedNpc.portrait) return "./nodata.png";
			return this.selectedNpc.portrait;
		},
	},
	watch: {
		// Every dossier opening, and every return to the overview, plays its entrance
		selectedNpc(npc) {
			this.$nextTick(() => {
				if (!npc) return this.introOverview(0);
				const dossier = this.$refs.dossier;
				if (!dossier) return;
				revealRecord(
					dossier.querySelector(".dossier-header"),
					dossier.querySelectorAll(
						".relationship-log, .dossier-clock-wrapper, .dossier-portrait-wrapper, .dossier-body"
					)
				);
				retrieveRows(dossier.querySelectorAll(".log-entry"), { startDelay: 0.5, gap: 0.08 });
			});
		},
	},
	mounted() {
		if (this.animate) this.introOverview(0.55);
		// Esc: close the dossier, then clear the search / faction filter
		this.offEscape = onEscape(() => {
			if (this.selectedNpc) return (this.selectedNpc = null), true;
			if (this.filtersActive) return this.clearFilters(), true;
			return false;
		});
	},
	beforeUnmount() {
		this.offEscape?.();
	},
	methods: {
		// Like Factions: the stat boxes wipe in and count up, then the cards cascade
		introOverview(base) {
			const stats = this.$refs.stats;
			const grid = this.$refs.overviewGrid;
			if (!stats || !grid || reducedMotion()) return;
			retrieveRows(stats.querySelectorAll(".overview-stat"), {
				startDelay: base,
				gap: 0.2,
				duration: 0.8,
			});
			stats
				.querySelectorAll(".overview-value")
				.forEach((el, i) =>
					countUp(el, Number(el.textContent) || 0, { delay: base + 0.3 + i * 0.05 })
				);
			animate(
				grid.querySelectorAll(".overview-card"),
				{ opacity: [0, 1], y: [12, 0] },
				{ duration: 0.45, delay: stagger(0.03, { startDelay: base + 0.35 }), ease: EASE_OUT }
			);
			// Relationship meters fill once their card is in
			grid.querySelectorAll(".overview-meter-fill").forEach((fill, i) => {
				fill.style.transformOrigin = "left center";
				animate(
					fill,
					{ scaleX: [0, 1] },
					{ duration: 0.7, delay: base + 0.6 + i * 0.03, ease: EASE_OUT }
				).finished.then(() =>
					setTimeout(() => (fill.style.transform = fill.style.transformOrigin = ""), 50)
				);
			});
		},
		selectNpc(npc) {
			// Clicking the open dossier's entry again returns to the overview
			this.selectedNpc = this.selectedNpc && this.selectedNpc.id === npc.id ? null : npc;
		},
		// Met during the Shadow of the Wolf college module that opened the campaign
		isSotw(npc) {
			return npc.tags.includes("SotW");
		},
		factionOf(npc) {
			return npc.affiliation || "Unaffiliated";
		},
		clearFilters() {
			this.search = "";
			this.factionFilter = "all";
		},
		npcZone(value) {
			if (value <= 3) return "Frosty";
			if (value <= 7) return "Neutral";
			return "Friendly";
		},
		npcMood(mood) {
			const parts = mood.split(", ");
			return parts.length > 1 ? parts.slice(1).join(", ") : parts[0];
		},
	},
};
</script>

<style scoped>
/* ── Single full-width panel ── */
.overview-toolbar {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.8em 1.6em;
	padding: 1.5em 1.5em 0;
}

.overview-toolbar .search-field {
	width: 340px;
	max-width: 100%;
}

.overview-toolbar .search-input {
	height: 34px;
	font-size: 13px;
}

.overview-toolbar .filter-group {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.7em;
}

.overview-toolbar .filter-pill {
	padding: 0.4em 0.9em;
	font-size: 11px;
}

.overview-toolbar .filter-select {
	width: auto;
	min-width: 200px;
	height: 32px;
}

/* Bigger cards so names fit */
.overview-grid {
	grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
	gap: 0.9em;
}

.overview-card {
	gap: 1em;
	padding: 0.9em 1em;
	align-items: center;
}

.overview-card .overview-portrait {
	width: 84px;
	height: 84px;
}

.overview-card h2 {
	font-size: 22px;
}

.npc-pronouns {
	flex-shrink: 0;
	font-family: "Share Tech Mono", monospace;
	font-size: 10px;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--text-pilot-code);
}

/* ── Filter Bar ── */
#npc-list .section-content-container {
	border-top: none;
	margin-top: 0;
}

/* ── List ── */
.npc-list-container {
	height: 100%;
}

.npc-item {
	cursor: pointer;
	margin: 0;
	margin-left: 1em;
	margin-right: 0.5em;
	margin-top: 0.5em;
	margin-bottom: 0.5em;
	padding-left: 0.75em;
	border-radius: 3px;
	transition: background-color 0.15s, border-color 0.15s;
}

.npc-item:hover {
	background-color: rgba(184, 151, 58, 0.12);
	border-left: 2px solid #b8973a;
}

.npc-item.active {
	background-color: rgba(184, 151, 58, 0.12);
	border-left: 2px solid #b8973a;
}

.npc-item h1 {
	margin: 0px;
	margin-bottom: 0.25em;
	line-height: 1.5em;
	font-family: "Raleway", sans-serif;
	font-size: 10px;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 3px;
	color: var(--text-mission-title);
}

.npc-item h2 {
	font-size: 18px;
	margin: 0%;
	font-family: "Big Shoulders Display", cursive;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: var(--text-mission-status);
}

.npc-title {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: var(--text-mission-status);
}

.npc-list-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 200px;
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.1em;
	color: var(--text-pilot-code);
	opacity: 0.4;
}

/* ── Dossier ── */
.npc-dossier {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: auto;
}

.dossier-top {
	display: flex;
	flex-direction: row;
	align-items: stretch;
	gap: 1.5em;
	padding: 1.5em;
	border-bottom: 1px solid var(--primary-color);
}

.dossier-left {
	display: flex;
	flex-direction: column;
	flex: 1;
	gap: 0.75em;
}

.dossier-portrait-wrapper {
	flex-shrink: 0;
	align-self: flex-start;
}

.dossier-portrait {
	width: 240px;
	height: 240px;
	object-fit: cover;
	object-position: top;
	border: 1px solid var(--primary-color);
}

.dossier-clock-wrapper {
	flex-shrink: 0;
	align-self: flex-start;
}

.dossier-body {
	padding: 0.75em 1.5em 1.5em;
}

.dossier-body :deep(blockquote) {
	margin: 0 0 1em 0;
	padding-left: 1em;
	border-left: 2px solid var(--primary-color);
	font-family: "Titillium Web", cursive;
	font-style: italic;
	font-size: 14px;
	color: #999696;
}

/* ── Relationship Log ── */
.relationship-log {
	border: 1px solid var(--primary-color);
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

.relationship-log-header {
	display: flex;
	align-items: center;
	gap: 0.5em;
	padding: 0.5em 0.75em;
	background: var(--primary-color);
	font-family: "Big Shoulders Display", cursive;
	font-size: 14px;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.1em;
}

.log-icon {
	width: 16px;
	height: 16px;
}

.relationship-log-entries {
	flex: 1;
	overflow: auto;
	padding: 0.5em;
}

.log-entry {
	display: flex;
	align-items: baseline;
	gap: 0.75em;
	padding: 0.35em 0.5em;
	border-bottom: 1px solid rgba(184, 151, 58, 0.15);
}

.log-entry:last-child {
	border-bottom: none;
}

.log-delta {
	font-family: "Share Tech Mono", monospace;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.05em;
	flex-shrink: 0;
	width: 2em;
	text-align: right;
}

.log-up {
	color: #3b7a57;
}

.log-down {
	color: #4a7a9b;
}

.log-text {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.05em;
	color: var(--text-pilot-code);
}

.log-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.15em;
	color: var(--text-pilot-code);
	opacity: 0.5;
}

/* ── Badges ── */
.npc-item-body {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 0.5em;
}

.npc-badges {
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 0.3em;
}

.npc-badges.dossier-badges {
	flex-direction: row;
	align-items: center;
}

/* Origin marker: met during the Shadow of the Wolf college module */
.origin-tag {
	padding: 0.1em 0.5em;
	background: rgba(122, 28, 46, 0.55);
	border: 1px solid var(--primary-color);
	font-family: "Share Tech Mono", monospace;
	font-size: 9px;
	letter-spacing: 0.12em;
	color: var(--text-pilot-value);
	cursor: help;
}

.status-badge.deceased {
	color: #9a9696;
}

.status-badge.unknown {
	color: #b8973a;
}

.npc-item.deceased h2,
.overview-card.deceased h2 {
	text-decoration: line-through;
	text-decoration-thickness: 1px;
	opacity: 0.6;
}

.overview-figure.alive {
	color: var(--text-pilot-value);
}
.overview-figure.deceased {
	color: #9a9696;
}
.overview-figure.unknown {
	color: #b8973a;
}
.zone-friendly {
	color: #3b7a57;
}
.zone-neutral {
	color: #b8973a;
}
.zone-frosty {
	color: #4a7a9b;
}

.npc-badges.overview-badges {
	flex-direction: row;
}

.overview-card.deceased .overview-portrait {
	filter: grayscale(1);
	opacity: 0.6;
}

/* Missing / unknown: faded out like the deceased, but not struck through */
.overview-card.unknown {
	border-style: dashed;
}

.overview-card.unknown .overview-portrait {
	filter: grayscale(0.85) blur(1px) brightness(0.8);
	opacity: 0.55;
}

.overview-card.unknown h2 {
	font-style: italic;
	opacity: 0.6;
}

/* Status stamped across the portrait */
.portrait-frame {
	position: relative;
	flex-shrink: 0;
	display: flex;
}

.portrait-stamp {
	position: absolute;
	left: 0;
	right: 0;
	/* Vertically centred with margins (a -50% translate can blur the text) */
	top: 0;
	bottom: 0;
	margin: auto 0;
	height: fit-content;
	padding: 0.2em 0.2em;
	text-align: center;
	font-family: "Share Tech Mono", monospace;
	font-size: 10px;
	line-height: 1.25;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	background: rgba(20, 20, 24, 0.82);
	border-top: 1px solid;
	border-bottom: 1px solid;
	pointer-events: none;
}

.portrait-stamp.deceased {
	color: #d1c8b8;
	border-color: rgba(209, 200, 184, 0.6);
}

.portrait-stamp.unknown {
	color: #b8973a;
	border-color: rgba(184, 151, 58, 0.7);
}

/* The global ".section-content-container *:last-child" rule drops this border */
.overview-stats > .overview-stat:last-child {
	border-bottom: 1px solid var(--primary-color);
}
</style>
